import React, { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Turnstile from './Turnstile';
import { TURNSTILE_SITE_KEY } from '../config';
import { VERSION_LEGAL } from './LegalView';

// Pantalla de acceso: iniciar sesión o crear cuenta (onboarding SaaS).
// Con `invitacion` ({ token, email, negocio }) es el registro de una persona
// invitada desde "Mi equipo": correo fijo y sin nombre de negocio.
// modoInicial y onVolver: "← Conocer Kontrol" lleva a la web (automatikaia.com/kontrol).
export default function LoginView({ invitacion = null, modoInicial = 'login', onVolver = null }) {
  const { iniciarSesion, registrarse, pedirRecuperacion } = useAuth();
  const [modo, setModo] = useState(invitacion ? 'registro' : modoInicial); // 'login' | 'registro' | 'recuperar'
  const [email, setEmail] = useState(invitacion?.email || '');
  const [password, setPassword] = useState('');
  const [nombreNegocio, setNombreNegocio] = useState('');
  // Si el enlace para cambiar la contraseña ya venció, Supabase vuelve con el error en la dirección.
  const [error, setError] = useState(() => (/error_code=otp_expired|error=access_denied/.test(window.location.hash)
    ? 'Ese enlace para cambiar tu contraseña ya venció. Pide uno nuevo en "¿Olvidaste tu contraseña?".' : null));
  const [enviado, setEnviado] = useState(false); // correo de recuperación enviado
  const [procesando, setProcesando] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [acepta, setAcepta] = useState(false);
  const turnstileRef = useRef(null);

  const traducirError = (msg = '') => {
    const m = msg.toLowerCase();
    if (m.includes('invalid login credentials')) return 'Correo o contraseña incorrectos.';
    if (m.includes('already registered')) return 'Ese correo ya tiene una cuenta. Inicia sesión.';
    if (m.includes('at least') && m.includes('charac')) return 'La contraseña debe tener al menos 8 caracteres.';
    if (m.includes('weak') || m.includes('pwned') || m.includes('compromised')) return 'Esa contraseña es muy común o insegura. Usa una diferente.';
    if (m.includes('valid email')) return 'Escribe un correo válido.';
    if (m.includes('rate limit')) return 'Demasiados intentos. Espera un minuto.';
    if (m.includes('captcha')) return 'No pudimos verificar que eres una persona. Intenta de nuevo.';
    return 'No se pudo completar. Revisa los datos e intenta de nuevo.';
  };

  const enviar = async (e) => {
    e.preventDefault();
    setError(null);
    if (modo === 'recuperar') {
      if (!email.trim()) { setError('Escribe el correo con el que entras a Kontrol.'); return; }
      if (TURNSTILE_SITE_KEY && !captcha) { setError('Espera un segundo a que se verifique que eres una persona.'); return; }
      setProcesando(true);
      const { error: err } = await pedirRecuperacion(email, captcha);
      setProcesando(false);
      turnstileRef.current?.reiniciar();
      if (err) { setError(traducirError(err.message)); return; }
      setEnviado(true);
      return;
    }
    if (!email.trim() || !password) {
      setError('Escribe tu correo y contraseña.');
      return;
    }
    if (modo === 'registro' && !invitacion && !nombreNegocio.trim()) {
      setError('Escribe el nombre de tu negocio.');
      return;
    }
    if (modo === 'registro' && !acepta) {
      setError('Para crear tu cuenta confirma que eres mayor de 18 años y aceptas los Términos y la Política de Privacidad.');
      return;
    }
    if (TURNSTILE_SITE_KEY && !captcha) {
      setError('Espera un segundo a que se verifique que eres una persona.');
      return;
    }
    setProcesando(true);
    const { error: err } =
      modo === 'login'
        ? await iniciarSesion(email, password, captcha)
        : await registrarse(email, password, nombreNegocio.trim(), captcha, VERSION_LEGAL, invitacion?.token);
    if (err) {
      setError(traducirError(err.message));
      setProcesando(false);
      turnstileRef.current?.reiniciar();
      return;
    }
    // Desde el enlace de invitación: ya con sesión, entrar a la app normal.
    if (invitacion) window.location.replace('/');
    // Si no hay error, AuthContext detecta la sesión y la app continúa.
  };

  const input = { width: '100%', padding: '12px', border: '1px solid #D1D5DB', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '12px' };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FDF2F8', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '32px', width: '400px', maxWidth: '100%', boxShadow: '0 8px 30px rgba(109,40,217,0.15)', boxSizing: 'border-box' }}>
        {onVolver && (
          <button type="button" onClick={onVolver} style={{ background: 'none', border: 'none', color: '#6D28D9', fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '12px', fontSize: '14px' }}>← Conocer Kontrol</button>
        )}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/logo192.png?v=2" alt="Kontrol" width="60" height="60"
            style={{ borderRadius: '14px', marginBottom: '8px', boxShadow: '0 4px 14px rgba(109,40,217,0.25)' }} />
          <h1 style={{ margin: 0, fontSize: '26px', color: '#6D28D9', letterSpacing: '0.5px' }}>Kontrol</h1>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9CA3AF' }}>by Automatika.IA</p>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#6B7280' }}>
            El sistema operativo de tu negocio
          </p>
        </div>

        {invitacion && (
          <div style={{ background: '#F5F3FF', borderRadius: '12px', padding: '12px', marginBottom: '16px', textAlign: 'center', fontSize: '14px', color: '#374151' }}>
            Te invitaron a unirte al equipo de <b>{invitacion.negocio}</b>.<br />
            Crea tu contraseña para entrar.
          </div>
        )}

        <form onSubmit={enviar}>
          {modo === 'registro' && !invitacion && (
            <input style={input} placeholder="Nombre de tu negocio (ej. Lolita's Store)"
              value={nombreNegocio} disabled={procesando}
              onChange={(e) => setNombreNegocio(e.target.value)} />
          )}
          <input style={input} type="email" placeholder="Correo electrónico"
            value={email} disabled={procesando} readOnly={!!invitacion} autoComplete="email"
            onChange={(e) => setEmail(e.target.value)} />
          {modo !== 'recuperar' && <input style={input} type="password" placeholder="Contraseña (mínimo 8 caracteres)"
            value={password} disabled={procesando}
            autoComplete={modo === 'login' ? 'current-password' : 'new-password'}
            onChange={(e) => setPassword(e.target.value)} />}
          {modo === 'login' && !invitacion && (
            <p style={{ margin: '-4px 0 12px', textAlign: 'right' }}>
              <button type="button" onClick={() => { setModo('recuperar'); setError(null); setEnviado(false); }}
                style={{ background: 'none', border: 'none', color: '#6D28D9', cursor: 'pointer', fontSize: '13px', padding: 0, fontWeight: 600 }}>
                ¿Olvidaste tu contraseña?
              </button>
            </p>
          )}
          {modo === 'recuperar' && (
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#4B5563', lineHeight: 1.5 }}>
              {enviado
                ? <>📩 Listo. Si <b>{email.trim()}</b> tiene una cuenta, te llegó un correo con el enlace para crear tu contraseña nueva. Revisa también la carpeta de spam.</>
                : 'Escribe el correo con el que entras a Kontrol y te mandamos un enlace para crear una contraseña nueva.'}
            </p>
          )}

          {modo === 'registro' && (
            <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: '#4B5563', marginBottom: '12px', cursor: 'pointer', lineHeight: 1.4 }}>
              <input type="checkbox" checked={acepta} disabled={procesando}
                onChange={(e) => setAcepta(e.target.checked)}
                style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: '#6D28D9', flexShrink: 0 }} />
              <span>
                Confirmo que soy <b>mayor de 18 años</b> y acepto los{' '}
                <a href="/terminos" target="_blank" rel="noopener noreferrer" style={{ color: '#6D28D9' }}>Términos y Condiciones</a> y la{' '}
                <a href="/privacidad" target="_blank" rel="noopener noreferrer" style={{ color: '#6D28D9' }}>Política de Privacidad</a>.
              </span>
            </label>
          )}

          <Turnstile ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY} onToken={setCaptcha} />

          {error && (
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#DC2626', backgroundColor: '#FEE2E2', padding: '10px', borderRadius: '8px' }}>
              ⚠️ {error}
            </p>
          )}

          <button type="submit" disabled={procesando}
            style={{ width: '100%', padding: '14px', backgroundColor: procesando ? '#9CA3AF' : '#6D28D9', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: procesando ? 'wait' : 'pointer' }}>
            {procesando ? '⏳ Un momento...' : modo === 'login' ? 'Entrar' : modo === 'recuperar' ? (enviado ? 'Enviar de nuevo' : 'Enviarme el enlace') : 'Crear mi cuenta'}
          </button>
        </form>

        {!invitacion && <p style={{ textAlign: 'center', margin: '16px 0 0', fontSize: '13px', color: '#6B7280' }}>
          {modo === 'login' ? '¿Primera vez aquí?' : modo === 'recuperar' ? '¿Ya la recordaste?' : '¿Ya tienes cuenta?'}{' '}
          <button onClick={() => { setModo(modo === 'login' ? 'registro' : 'login'); setError(null); setEnviado(false); }}
            style={{ background: 'none', border: 'none', color: '#6D28D9', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px', padding: 0 }}>
            {modo === 'login' ? 'Crea tu cuenta' : 'Inicia sesión'}
          </button>
        </p>}
        <p style={{ textAlign: 'center', margin: '14px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
          <a href="/terminos" style={{ color: '#9CA3AF' }}>Términos</a> · <a href="/privacidad" style={{ color: '#9CA3AF' }}>Privacidad</a> · <a href="/reembolsos" style={{ color: '#9CA3AF' }}>Reembolsos</a>
        </p>
      </div>
    </div>
  );
}
